import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { DomainSystem } from '@/core/app/dispatch';
import type { InstanceId, NpcId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { ShootCriterion, StudioShootDef } from '@/core/content/schemas/studio';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave, ShootGrade } from '@/core/persistence/schema/v1';
import { evaluateAll } from './conditions';
import { computeCoziness } from './coziness';
import { addFriendship } from './friendship';
import { grantRewards } from './rewards';
import type { HandlerEnv } from './placement/handlers';
import { createRng } from './rng';
import { studioLevelFor } from './studioLevel';

/** Studio NANO (docs/design/studio-nano.md §4): bookings, the set checklist and the always-kind grades. */
export const STUDIO_UNLOCK = 'unl_studio';
const FIRST_SHOOT_NPC: NpcId = 'npc_bao';
/** Who books first once the studio is open (chapter 5 order: Bảo's profile photo, then Sếp Mèo). */
const FIRST_SHOOT_ORDER: readonly NpcId[] = ['npc_bao', 'npc_mit', 'npc_lan'];

export const studioOpen = (s: GameSave) => s.unlockedIds.includes(STUDIO_UNLOCK);

export { studioLevelFor };

export function studioLevel(content: ContentRegistry, s: GameSave): number {
  return studioLevelFor(content, s.studio.reputation);
}

/** Points still needed for the next Tiếng lành level, or null at the top. */
export function studioNextLevel(
  content: ContentRegistry,
  s: GameSave,
): { level: number; points: number; need: number; prev: number } | null {
  const levels = content.balance.studio.levels;
  const level = studioLevel(content, s);
  const need = levels[level];
  if (need === undefined) return null;
  return { level: level + 1, points: s.studio.reputation, need, prev: levels[level - 1] ?? 0 };
}

/** Everything that stands in the studio: floor and wall placements plus things on surfaces. */
function placedInStudio(s: GameSave): InstanceId[] {
  const room = s.rooms.studio;
  return [
    ...Object.keys(room.placements),
    ...Object.values(room.slots).flatMap((c) => (c.kind === 'instance' ? [c.instanceId] : [])),
  ] as InstanceId[];
}

export interface CriterionProgress {
  criterion: ShootCriterion;
  have: number;
  need: number;
  met: boolean;
}

/** Live checklist for a brief: what the studio has right now against what the client asked for. */
export function shootProgress(
  content: ContentRegistry,
  s: GameSave,
  def: StudioShootDef,
): CriterionProgress[] {
  const defs = placedInStudio(s).flatMap((id) => {
    const d = content.items.get(s.inventory.instances[id]?.itemId ?? 'dec_none');
    return d ? [d] : [];
  });
  return def.criteria.map((criterion) => {
    let have: number;
    let need = 1;
    switch (criterion.type) {
      case 'theme':
        have = defs.filter((d) => d.themeTags.includes(criterion.tag)).length;
        need = criterion.count;
        break;
      case 'tag':
        have = defs.filter((d) => d.tags.includes(criterion.tag)).length;
        need = criterion.count;
        break;
      case 'item':
        have = defs.filter((d) => d.id === criterion.itemId).length;
        break;
      case 'wallFloor': {
        const room = s.rooms.studio;
        have = criterion.ids.includes(room.wallId) || criterion.ids.includes(room.floorId) ? 1 : 0;
        break;
      }
      case 'cozy':
        have = computeCoziness(content, s, 'studio').total;
        need = criterion.points;
        break;
    }
    return { criterion, have: Math.min(have, need), need, met: have >= need };
  });
}

/** Only praise (plan §2): all met = Xuất sắc, at least half = Rất dễ thương, anything else = Dễ thương. */
export function gradeFor(met: number, total: number): ShootGrade {
  if (total > 0 && met >= total) return 'excellent';
  if (met * 2 >= total && met > 0) return 'very_cute';
  return 'cute';
}

const round5 = (n: number) => Math.max(5, Math.round(n / 5) * 5);

export function shootCoins(content: ContentRegistry, def: StudioShootDef, grade: ShootGrade): number {
  const S = content.balance.studio;
  return round5((def.rewardCoins ?? S.rewardCoins) * S.gradeCoinMultiplier[grade]);
}

/** Create one booking if a met neighbour has none. Deterministic: uses studio.rngState. */
export function createShootRequest(
  draft: GameSave,
  content: ContentRegistry,
  preferNpc?: NpcId,
): DomainEvent[] {
  const S = draft.studio;
  if (S.requests.length >= content.balance.studio.maxOpenRequests) return [];
  const cands = [...content.npcs.values()]
    .filter((n) => n.kind === 'human' && draft.flags[n.metFlag] === true)
    .filter((n) => !S.requests.some((r) => r.npcId === n.id) && S.session?.npcId !== n.id)
    .map((n) => n.id);
  if (cands.length === 0) return [];
  const rng = createRng(S.rngState);
  const fresh = FIRST_SHOOT_ORDER.filter((id) => cands.includes(id) && !S.firstShootDone[id]);
  const npcId: NpcId = preferNpc && cands.includes(preferNpc) ? preferNpc : (fresh[0] ?? rng.pick(cands));
  const mine = [...content.studioShoots.values()].filter((d) => d.npcId === npcId);
  const first = S.firstShootDone[npcId] ? undefined : mine.find((d) => d.isFirst);
  const pool = mine.filter((d) => !d.isFirst && evaluateAll(content, draft, d.conditions).ok);
  const def = first ?? (pool.length > 0 ? rng.weighted(pool, (d) => d.weight) : undefined);
  S.rngState = rng.state;
  if (!def) return [];
  S.seq += 1;
  const requestId = `sr_${String(S.seq)}` as const;
  S.requests.push({ requestId, shootId: def.id, npcId, createdAtPlayMs: draft.clock.playTimeMs });
  return [{ type: 'SHOOT_REQUESTED', requestId, shootId: def.id, npcId }];
}

function scheduleNext(draft: GameSave, content: ContentRegistry): void {
  const S = draft.studio;
  S.nextRequestAtPlayMs =
    S.requests.length < content.balance.studio.maxOpenRequests
      ? draft.clock.playTimeMs + content.balance.studio.requestIntervalSec * 1000
      : null;
}

/** Called from advanceTime: a new booking every requestIntervalSec of play while the Sổ hẹn has room. */
export function studioTick(draft: GameSave, env: HandlerEnv): DomainEvent[] {
  if (!studioOpen(draft)) return [];
  const S = draft.studio;
  const t = draft.clock.playTimeMs;
  if (S.requests.length >= env.content.balance.studio.maxOpenRequests) {
    S.nextRequestAtPlayMs = null;
    return [];
  }
  if (S.nextRequestAtPlayMs === null) {
    S.nextRequestAtPlayMs = t + env.content.balance.studio.requestIntervalSec * 1000;
    return [];
  }
  if (t < S.nextRequestAtPlayMs) return [];
  const events = createShootRequest(draft, env.content);
  scheduleNext(draft, env.content);
  return events;
}

/**
 * Opening the studio (unl_studio): its paper wall and parquet become the player's, and Anh Bảo books the first
 * shoot right away (chapter 5). Also reconciles on boot, so an interrupted grant never leaves it empty.
 */
export const studioSystem: DomainSystem = (draft, events, env) => {
  if (!studioOpen(draft)) return [];
  const opened = events.some(
    (e) => (e.type === 'UNLOCK_GRANTED' && e.unlockId === STUDIO_UNLOCK) || e.type === 'SESSION_BOOTED',
  );
  if (!opened) return [];
  const layout = env.content.rooms.get('studio');
  for (const id of layout ? [layout.defaultWall, layout.defaultFloor] : [])
    if (!draft.ownedWallFloors.includes(id)) draft.ownedWallFloors.push(id);
  const S = draft.studio;
  if (S.seq > 0) return [];
  const out = createShootRequest(draft, env.content, FIRST_SHOOT_NPC);
  scheduleNext(draft, env.content);
  return out;
};

export function startShoot(draft: GameSave, cmd: CommandOf<'startShoot'>): CommandResult {
  if (!studioOpen(draft)) return { ok: false, error: 'LOCKED' };
  const S = draft.studio;
  if (S.session) return { ok: false, error: 'LIMIT_REACHED' };
  const req = S.requests.find((r) => r.requestId === cmd.requestId);
  if (!req) return { ok: false, error: 'NOT_FOUND' };
  S.session = { requestId: req.requestId, shootId: req.shootId, npcId: req.npcId, phase: 'setup' };
  return {
    ok: true,
    events: [{ type: 'SHOOT_STARTED', requestId: req.requestId, shootId: req.shootId, npcId: req.npcId }],
  };
}

/** "Để sau" during setup: the client steps off the set, the booking stays in the Sổ hẹn. */
export function cancelShoot(draft: GameSave): CommandResult {
  const S = draft.studio;
  if (S.session?.phase !== 'setup') return { ok: false, error: 'NOT_FOUND' };
  const { requestId, npcId } = S.session;
  S.session = null;
  return { ok: true, events: [{ type: 'SHOOT_ENDED', requestId, npcId, completed: false }] };
}

/** "Chụp!": grade the set as it stands, pay, and keep the client posing for a souvenir photo. */
export function finishShoot(draft: GameSave, cmd: CommandOf<'finishShoot'>, env: HandlerEnv): CommandResult {
  const S = draft.studio;
  const session = S.session;
  if (session?.phase !== 'setup') return { ok: false, error: 'NOT_FOUND' };
  const def = env.content.studioShoots.get(session.shootId);
  if (!def) return { ok: false, error: 'NOT_FOUND' };
  const progress = shootProgress(env.content, draft, def);
  const met = progress.filter((p) => p.met).length;
  const grade = gradeFor(met, progress.length);
  const B = env.content.balance.studio;
  const coins = shootCoins(env.content, def, grade);
  const reputation = B.reputation[grade];
  const friendship = B.friendship + (grade === 'excellent' ? B.excellentFriendshipBonus : 0);

  draft.wallet.coins += coins;
  draft.shop.ledgerSummary.rewardsCoinsTotal += coins;
  env.ledger?.push({
    txId: cmd.txId,
    saveId: draft.saveId,
    atWallMs: env.nowWallMs,
    atPlayMs: draft.clock.playTimeMs,
    kind: 'reward',
    coinsDelta: coins,
    balanceAfter: draft.wallet.coins,
    refs: { requestId: session.requestId, shootId: def.id, grade },
  });
  const levelBefore = studioLevel(env.content, draft);
  S.reputation += reputation;
  const levelAfter = studioLevel(env.content, draft);
  S.firstShootDone[session.npcId] = true;
  S.requests = S.requests.filter((r) => r.requestId !== session.requestId);
  S.session = { ...session, phase: 'done', grade };
  if (S.nextRequestAtPlayMs === null) scheduleNext(draft, env.content);
  return {
    ok: true,
    events: [
      {
        type: 'COINS_CHANGED',
        delta: coins,
        balance: draft.wallet.coins,
        reason: 'shoot',
        ref: session.requestId,
      },
      {
        type: 'SHOOT_COMPLETED',
        txId: cmd.txId,
        requestId: session.requestId,
        shootId: def.id,
        npcId: session.npcId,
        grade,
        met,
        total: progress.length,
        coins,
        reputation,
      },
      { type: 'STUDIO_REPUTATION_CHANGED', delta: reputation, total: S.reputation, level: levelAfter },
      ...levelRewards(draft, env.content, levelBefore, levelAfter),
      ...addFriendship(draft, env.content, session.npcId, 'shoot', env.nowWallMs, friendship),
    ],
  };
}

/** Each newly reached Tiếng lành level pays its rewards once (key studio:L<n>), like Coziness levels. */
function levelRewards(draft: GameSave, content: ContentRegistry, from: number, to: number): DomainEvent[] {
  const out: DomainEvent[] = [];
  for (let L = from + 1; L <= to; L++) {
    const rewards = [...(content.studioLevelRewards[L - 1] ?? [])];
    out.push({ type: 'STUDIO_LEVEL_REACHED', level: L, rewards });
    out.push(...grantRewards(draft, content, `studio:L${String(L)}`, rewards));
  }
  return out;
}

/** "Xong" on the result card: the client says bye and the set is free again. */
export function endShoot(draft: GameSave): CommandResult {
  const S = draft.studio;
  if (S.session?.phase !== 'done') return { ok: false, error: 'NOT_FOUND' };
  const { requestId, npcId } = S.session;
  S.session = null;
  return { ok: true, events: [{ type: 'SHOOT_ENDED', requestId, npcId, completed: true }] };
}

/** "Để sau" in the Sổ hẹn: the booking goes away, no penalty; a first booking comes back later. */
export function skipShootRequest(
  draft: GameSave,
  cmd: CommandOf<'skipShootRequest'>,
  env: HandlerEnv,
): CommandResult {
  const S = draft.studio;
  const req = S.requests.find((r) => r.requestId === cmd.requestId);
  if (!req) return { ok: false, error: 'NOT_FOUND' };
  if (S.session?.requestId === req.requestId) return { ok: false, error: 'NOT_REMOVABLE' };
  S.requests = S.requests.filter((r) => r !== req);
  if (S.nextRequestAtPlayMs === null) scheduleNext(draft, env.content);
  return { ok: true, events: [{ type: 'SHOOT_SKIPPED', requestId: req.requestId, npcId: req.npcId }] };
}

/** Who stands on the pose mark right now (the booker, or Sếp Mèo for Bé Mít's cat shoots). */
export function posingModel(content: ContentRegistry, s: GameSave): NpcId | null {
  const session = s.studio.session;
  if (!session) return null;
  return content.studioShoots.get(session.shootId)?.model ?? session.npcId;
}
